import { DirectiveOptions } from 'vue'

interface ColumnResizeOptions {
  minWidth?: number;
  maxWidth?: number;
  onResize?: (width: number, columnId: string) => void;
  storageKey?: string;
  columnId?: string;
}

const COLUMN_WIDTH_STORAGE_KEYS = ['tariff_column_widths', 'tariff_columns'];

function getMergedSavedWidths(): Record<string, number> {
  const merged: Record<string, number> = {};

  COLUMN_WIDTH_STORAGE_KEYS.forEach((storageKey) => {
    const savedWidths = JSON.parse(localStorage.getItem(storageKey) || '{}');
    Object.assign(merged, savedWidths);
  });

  return merged;
}

function applyWidthToElement(el: HTMLElement, width: number) {
  el.style.width = `${width}px`;
  el.style.minWidth = `${width}px`;
}

export function reapplyAllColumnWidths() {
  const savedWidths = getMergedSavedWidths();

  Object.entries(savedWidths).forEach(([columnId, width]) => {
    document.querySelectorAll(`[data-column-id="${columnId}"]`).forEach((element) => {
      applyWidthToElement(element as HTMLElement, width);
    });
  });
}

export const columnResize: DirectiveOptions = {
  bind(el: HTMLElement, binding) {
    const options: ColumnResizeOptions = binding.value || {};
    const minWidth = options.minWidth || 50;
    const maxWidth = options.maxWidth || 500;
    const storageKey = options.storageKey || 'tariff_column_widths';

    const columnId = options.columnId ||
                    el.getAttribute('data-column-id') ||
                    `column_${Math.random().toString(36).substr(2, 9)}`;

    el.setAttribute('data-column-id', columnId);

    const findAllColumnElements = (): HTMLElement[] => {
      const elements: HTMLElement[] = [];
      const allElements = document.querySelectorAll('[data-column-id]');

      allElements.forEach((element: Element) => {
        if ((element as HTMLElement).getAttribute('data-column-id') === columnId) {
          elements.push(element as HTMLElement);
        }
      });

      return elements;
    };

    const applyWidthToAllColumnElements = (width: number) => {
      const allElements = findAllColumnElements();
      allElements.forEach((element) => {
        applyWidthToElement(element, width);
      });
    };

    const applySavedWidthToElement = () => {
      const savedWidth = getMergedSavedWidths()[columnId];
      if (savedWidth) {
        applyWidthToElement(el, savedWidth);
      }
    };

    const resizeHandle = document.createElement('div');
    resizeHandle.style.position = 'absolute';
    resizeHandle.style.right = '-2px';
    resizeHandle.style.top = '0';
    resizeHandle.style.width = '4px';
    resizeHandle.style.height = '100%';
    resizeHandle.style.cursor = 'col-resize';
    resizeHandle.style.zIndex = '1000';
    resizeHandle.style.backgroundColor = 'transparent';

    el.appendChild(resizeHandle);

    let isResizing = false;
    let startX = 0;
    let startWidth = 0;

    const startResize = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      isResizing = true;
      startX = e.clientX;
      startWidth = el.offsetWidth;

      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResize);

      el.classList.add('resizing');
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };

    const resize = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = e.clientX - startX;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));

      applyWidthToAllColumnElements(newWidth);
    };

    const stopResize = () => {
      if (!isResizing) return;

      isResizing = false;
      el.classList.remove('resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);

      const currentWidth = el.offsetWidth;
      const savedWidths = JSON.parse(localStorage.getItem(storageKey) || '{}');
      savedWidths[columnId] = currentWidth;
      localStorage.setItem(storageKey, JSON.stringify(savedWidths));

      if (storageKey !== 'tariff_column_widths') {
        const primaryWidths = JSON.parse(localStorage.getItem('tariff_column_widths') || '{}');
        primaryWidths[columnId] = currentWidth;
        localStorage.setItem('tariff_column_widths', JSON.stringify(primaryWidths));
      }

      if (options.onResize) {
        options.onResize(currentWidth, columnId);
      }
    };

    resizeHandle.addEventListener('mousedown', startResize);

    (el as any)._columnResizeHandlers = {
      resizeHandle,
      startResize,
      resize,
      stopResize,
      applySavedWidthToElement
    };
  },

  inserted(el: HTMLElement) {
    const handlers = (el as any)._columnResizeHandlers;
    if (handlers?.applySavedWidthToElement) {
      handlers.applySavedWidthToElement();
    }
  },

  unbind(el: HTMLElement) {
    const handlers = (el as any)._columnResizeHandlers;
    if (handlers) {
      handlers.resizeHandle.removeEventListener('mousedown', handlers.startResize);
      document.removeEventListener('mousemove', handlers.resize);
      document.removeEventListener('mouseup', handlers.stopResize);

      if (handlers.resizeHandle.parentNode) {
        handlers.resizeHandle.parentNode.removeChild(handlers.resizeHandle);
      }
    }
  }
}
